import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { GlobalState } from '@app/store/store';
import { Button } from '@infocert/ng-ui-kit/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { validPhoneNumberValidator } from '@app/components/commons/forms/formly-validators';
import { DropdownItem } from '@infocert/ng-ui-kit/models';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Subscription, tap } from 'rxjs';

const phoneNumberRequired: ValidatorFn = (control: AbstractControl) => {
  const value = control?.value;
  const number =
    typeof value === 'object' && value !== null
      ? (value.number ?? '')
      : String(value ?? '');
  return number && number.trim() ? null : { required: true };
};

interface MigrationStep {
  step: number;
  title: string;
  description: string;
  open?: boolean;
  disabled?: boolean;
}
@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class OtpPageComponent implements OnInit {
  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<GlobalState>,
    private certificateService: CertificateService,
    private otpService: OtpService,
    private toasterService: ToastrService,
    private translateService: TranslateService,

    private router: Router
  ) {}

  loading: boolean = false;
  notTrissAttempted = false;
  trissPhoneAttempted = false;
  trissOtpAttempted = false;
  private validationAttemptSubscriptions: Subscription[] = [];
  currentStep: number = 0;
  automatic: boolean = false;
  isEseal: boolean = false;
  isIcssUser: boolean = false;
  isTrissUser: boolean | undefined = false;
  isPkBox: boolean = false;
  //error_state = false; // variabile per controllare se c'è stato un errore

  showSuccess: boolean = false;
  showHWSuccess: boolean = false;
  showWarning: boolean = true;
  showRevoke: boolean = false;
  hasApp: boolean = false;
  canUseLongTermToken: boolean | undefined = false;

  // Proprietà per le modali standalone
  appModalVisible: boolean = false;
  smsModalVisible: boolean = false;
  hardwareModalVisible: boolean = false;
  virtualModalVisible: boolean = false;

  otpType: string = '';
  telephone: string = '';
  newMobile: string = ''; //nuovo numero a cui vogliamo passare
  deviceType: string = '';
  _deviceName: string = '';
  deviceUser: string = '';
  _devices: any[] = [];
  selectedDevice: any = {};
  number_text: string = ''; //valore telephoneInput
  successText: string = '';

  // Country options for prefix number input
  countryOptions: DropdownItem[] = [
    { value: '+39', label: 'Italia', flag: 'it' },
    { value: '+1', label: '+1', flag: 'us' },
    { value: '+44', label: '+44', flag: 'gb' },
    { value: '+33', label: '+33', flag: 'fr' },
    { value: '+49', label: '+49', flag: 'de' },
    { value: '+34', label: '+34', flag: 'es' },
    { value: '+31', label: '+31', flag: 'nl' },
    { value: '+32', label: '+32', flag: 'be' },
    { value: '+41', label: '+41', flag: 'ch' },
    { value: '+43', label: '+43', flag: 'at' },
  ];

  // Default country (Italy)
  defaultCountry: DropdownItem = this.countryOptions[0];

  // otp form

  phoneInitiated = false;
  // validatePhoneInput: boolean = false;

  //form primo step del flusso triss
  telephoneInput = new UntypedFormControl(
    {
      prefix: '+39',
      number: '',
    },
    [
      phoneNumberRequired,
      (control: AbstractControl): ValidationErrors | null =>
        validPhoneNumberValidator(control),
    ]
  );

  telephoneForm = new UntypedFormGroup({
    telephoneInput: this.telephoneInput,
  });

  //form secondo step del flusso triss
  otp = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  confirmation_code = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  otpForm = new UntypedFormGroup({
    otp: this.otp,
    confirmation_code: this.confirmation_code,
  });

  //form flusso non triss
  notTrissTelephoneInput = new UntypedFormControl(
    {
      prefix: '+39',
      number: '',
    },
    [
      phoneNumberRequired,
      (control: AbstractControl): ValidationErrors | null =>
        validPhoneNumberValidator(control),
    ]
  );

  notTrissOtp = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);
  notTrissOtpForm = new UntypedFormGroup({
    notTrissTelephoneInput: this.notTrissTelephoneInput,
    notTrissOtp: this.notTrissOtp,
  });

  // convenience getter for easy access to form fields

  get fTelephone() {
    return this.telephoneForm.controls;
  }

  get telephoneFormErrors() {
    const errors = this.telephoneForm.errors;
    return errors;
  }
  get fOtp() {
    return this.otpForm.controls;
  }

  get otpFormErrors() {
    const errors = this.otpForm.errors;
    return errors;
  }

  get fNotTrissOtp() {
    return this.notTrissOtpForm.controls;
  }

  get notTrissotpFormErrors() {
    const errors = this.notTrissOtpForm.errors;
    return errors;
  }

  get newTelephoneForConfirmation(): string {
    const phoneValue = this.fTelephone?.['telephoneInput']?.value;
    if (!phoneValue) {
      return '';
    }
    const prefix = phoneValue.prefix ?? '';
    const number = phoneValue.number ?? '';
    return `${prefix}${number}`.replace(/\s+/g, '');
  }

  // hardware form

  hwOtp1 = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  hwOtp2 = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  hwForm = new UntypedFormGroup({
    hwOtp1: this.hwOtp1,
    hwOtp2: this.hwOtp2,
  });

  // convenience getter for easy access to form fields
  get fHW() {
    return this.hwForm.controls;
  }

  get hwFormErrors() {
    const errors = this.hwForm.errors;
    return errors;
  }

  get showAppCard(): boolean {
    if (
      this.otpType === 'HARDWARE' ||
      this.otpType === 'VASCO' ||
      this.otpType === 'EXTERNAL'
    ) {
      return false;
    } else {
      return true;
    }
  }

  // ViewChild non più necessari con le modali standalone

  get iconText(): string {
    return this.hasApp ? 'COMMON.ACTIVE' : 'COMMON.NEEDS_ACTIVATE';
  }

  get appCardTitle(): string {
    return this.isPkBox ? 'OTP_PAGE.GOSIGN_MOBILE.TITLE' : 'OTP_PAGE.APP.TITLE';
  }

  get appBadgeText(): string {
    if (this.isPkBox) {
      return 'OTP_PAGE.GOSIGN_MOBILE.ICON_TEXT';
    } else {
      return 'OTP_PAGE.APP.ICON_TEXT';
    }
  }

  get appModalTitle(): string {
    return this.isPkBox ? 'OTP_PAGE.GOSIGN_MOBILE.TITLE' : 'OTP_PAGE.APP.TITLE';
  }

  get appModalDesc(): string {
    return this.isPkBox
      ? 'OTP_PAGE.GOSIGN_MOBILE.MODAL_DESC'
      : 'OTP_PAGE.APP.MODAL_DESC';
  }

  get appStepOne(): string {
    return this.isPkBox
      ? 'OTP_PAGE.GOSIGN_MOBILE.STEP_ONE'
      : 'OTP_PAGE.APP.STEP_ONE';
  }

  get appStepTwo(): string {
    return this.isPkBox
      ? 'OTP_PAGE.GOSIGN_MOBILE.STEP_TWO'
      : 'OTP_PAGE.APP.STEP_TWO';
  }

  get appStepThree(): string {
    return this.isPkBox
      ? 'OTP_PAGE.GOSIGN_MOBILE.STEP_THREE'
      : 'OTP_PAGE.APP.STEP_THREE';
  }

  get googlePlayUrl(): string {
    return this.isPkBox
      ? 'https://play.google.com/store/apps/details?id=it.infocert.mobile.dike&hl=it&gl=US'
      : 'https://play.google.com/store/apps/details?id=it.infocert.myinfocert.phoenix&hl=it&gl=US';
  }

  get appStoreUrl(): string {
    return this.isPkBox
      ? 'https://apps.apple.com/it/app/gosign/id469062333'
      : 'https://apps.apple.com/it/app/myinfocert/id1300573997';
  }

  get deviceModalDesc(): string {
    return this.isPkBox
      ? 'OTP_PAGE.GOSIGN_MOBILE.BIND_MODAL.DESC'
      : 'OTP_PAGE.APP.BIND_MODAL.DESC';
  }

  get filteredDevices(): any[] {
    if (this.isPkBox) {
      return this._devices;
    } else {
      return this._devices.filter(
        device =>
          device.subAuth !== 'SMS_PLAIN' && device.bindStatus === 'active'
      );
    }
  }

  get revokeConfirmText(): string {
    if (this.isPkBox) {
      return this.translateService.instant(
        'OTP_PAGE.GOSIGN_MOBILE.BIND_MODAL.REVOKE_DESC',
        {
          subAuth: this.translateService.instant(
            'OTP_PAGE.GOSIGN_MOBILE.BIND_MODAL.FIDO'
          ),
          deviceName: this.selectedDevice.deviceName,
        }
      );
    } else {
      return this.translateService.instant(
        'OTP_PAGE.APP.BIND_MODAL.REVOKE_DESC',
        {
          subAuth: this.translateService.instant(
            'OTP_PAGE.APP.BIND_MODAL.' + this.selectedDevice.subAuth
          ),
          deviceType: this.selectedDevice.deviceType,
          user: this.selectedDevice.userId,
          deviceName: this.selectedDevice.deviceName,
        }
      );
    }
  }

  get hardwareIconText(): string {
    return this.showHWSuccess
      ? 'OTP_PAGE.HARDWARE.SYNCED_DESC'
      : 'OTP_PAGE.HARDWARE.NEEDS_SYNC';
  }

  get cardAppButton(): Button {
    return {
      label: this.translateService.instant(
        this.hasApp ? 'COMMON.BUTTON.MANAGE' : 'COMMON.ACTIVATE'
      ),
      color: 'text',
      onClick: () => this.openModal('app'),
    };
  }

  get cardSmsButton(): Button {
    const label = this.translateService.instant('OTP_PAGE.SMS.CHANGE_NUMBER');
    return {
      label: label,
      altText: label,
      color: 'text',
      onClick: () => this.openModal('sms'),
    };
  }

  get cardHardwareButton(): Button {
    const label = this.translateService.instant('OTP_PAGE.HARDWARE.SYNC');
    return {
      label: label,
      altText: label,
      color: 'text',
      onClick: () => this.openModal('hardware'),
    };
  }

  get cardVirtualButton(): Button {
    const label = this.translateService.instant('COMMON.BUTTON.MANAGE');
    return {
      label: label,
      altText: label,
      color: 'text',
      onClick: () => {
        this.goToVirtualTokenPage();
        //this.openVirtualModal();
      },
    };
  }

  // modalRef non più necessario con le modali standalone
  // public modalRef: BsModalRef | undefined;

  // Metodi per gestire le modali
  openModal(type: 'app' | 'sms' | 'hardware' | 'virtual'): void {
    switch (type) {
      case 'app':
        this.appModalVisible = true;
        break;
      case 'sms':
        this.notTrissAttempted = false;
        this.trissPhoneAttempted = false;
        this.trissOtpAttempted = false;
        if (!this.isTrissUser) this.sendOtp();
        this.smsModalVisible = true;
        break;
      case 'hardware':
        this.hardwareModalVisible = true;
        break;
      case 'virtual':
        this.virtualModalVisible = true;
        break;
    }
  }

  // Metodi per gestire il toggle delle modali
  onToggleModal(
    visible: boolean,
    type: 'app' | 'sms' | 'hardware' | 'virtual'
  ): void {
    const modalProperty = `${type}ModalVisible` as keyof this;
    (this as any)[modalProperty] = visible;

    if (!visible) {
      this.closeModal();
    }
  }

  onToggleAppModal = (visible: boolean) => this.onToggleModal(visible, 'app');
  onToggleSmsModal = (visible: boolean) => this.onToggleModal(visible, 'sms');
  onToggleHardwareModal = (visible: boolean) =>
    this.onToggleModal(visible, 'hardware');
  onToggleVirtualModal = (visible: boolean) =>
    this.onToggleModal(visible, 'virtual');

  private setupValidationAttemptReset(): void {
    this.validationAttemptSubscriptions.forEach(sub => sub.unsubscribe());
    this.validationAttemptSubscriptions = [];

    this.validationAttemptSubscriptions.push(
      this.notTrissTelephoneInput.valueChanges.subscribe(() => {
        if (this.notTrissAttempted) this.notTrissAttempted = false;
      })
    );
    this.validationAttemptSubscriptions.push(
      this.notTrissOtp.valueChanges.subscribe(() => {
        if (this.notTrissAttempted) this.notTrissAttempted = false;
      })
    );
    this.validationAttemptSubscriptions.push(
      this.telephoneInput.valueChanges.subscribe(() => {
        if (this.trissPhoneAttempted) this.trissPhoneAttempted = false;
      })
    );
    this.validationAttemptSubscriptions.push(
      this.otp.valueChanges.subscribe(() => {
        if (this.trissOtpAttempted) this.trissOtpAttempted = false;
      })
    );
    this.validationAttemptSubscriptions.push(
      this.confirmation_code.valueChanges.subscribe(() => {
        if (this.trissOtpAttempted) this.trissOtpAttempted = false;
      })
    );
  }

  ngOnInit(): void {
    this.getOtpType();

    this.telephoneInput.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => {
          if (!this.phoneInitiated) {
            this.telephoneInput.markAsPristine();
            this.phoneInitiated = true;
          }
        })
      )
      .subscribe();
    this.notTrissTelephoneInput.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => {
          if (!this.phoneInitiated) {
            this.notTrissTelephoneInput.markAsPristine();
            this.phoneInitiated = true;
          }
        })
      )
      .subscribe();
    this.setupValidationAttemptReset();
    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          if (store.session) {
            this.automatic = store.session.automatic;
            this.isIcssUser = store.session.icssUser;
            this.isTrissUser = store.session?.trissUser;
            this.isPkBox = !store.session.icssUser && !store.session.trissUser;
            this.isEseal = store.session.eseal;
            this.canUseLongTermToken =
              (this.automatic || this.isEseal) &&
              (this.isIcssUser || this.isTrissUser);
          }

          if (store.certificate) {
            if (store.certificate.otpType) {
              this.otpType = store.certificate.otpType;
            }
            if (store.certificate.telephone !== ' ') {
              this.telephone = store.certificate.telephone;
            }
            this.getPushDevices(store.certificate.telephone);
          }
        })
      )
      .subscribe();

    // check se url è prod e abilitare le validazioni
    if (
      window.location.href.split('?')[0].split('#')[0] ===
      'https://mysign.infocert.it/'
    ) {
      // LASCIARE FALSE PER ALTRI AMBIENTI PER PERMETTERE TEST AUTOMATICI
      // this.validatePhoneInput = true;
    }
  }

  async getOtpType() {
    const certificates = await lastValueFrom(
      this.certificateService.getCertificateList()
    );
    this.otpType = certificates[0].otpType;
  }

  get devices(): any[] {
    return this._devices;
  }

  async getPushDevices(telephone: string = '') {
    this._devices = await lastValueFrom(
      this.certificateService.getPushDevices()
    );

    // reload della pagina in caso viene scollegato l'ultimo bind esistente
    if (this.hasApp && this._devices.length < 1) {
      location.reload();
    }

    // dispositivi collegati a MYINFOCERT (ISAC) o GOSIGN MOBILE (PkBox)
    if (
      this._devices.length > 1 ||
      (this._devices.length === 1 && this.isPkBox)
    ) {
      this.hasApp = true;

      // Se esiste il bind si prende il numero di telefono del bind
      for (let i = 0; i < this._devices.length; i++) {
        if (this._devices[i].subAuth === 'SMS_PLAIN')
          this.telephone = this._devices[i].deviceId;
        if (this._devices[i].deviceName !== '') {
          this._deviceName = this._devices[i].deviceName;
        }
        if (this._devices[i].deviceType !== '') {
          this.deviceType = this._devices[i].deviceType;
        }
        if (this._devices[i].userId !== '') {
          this.deviceUser = this._devices[i].userId;
        }
      }
    } else {
      if (telephone === ' ') this.telephone = this._devices[0].deviceId;
    }
  }

  revokeDevice() {
    this.loading = true;
    let bindId = '';
    if ((!this.isIcssUser && !this.isTrissUser) || this.isTrissUser) {
      bindId = this.selectedDevice.deviceId;
    } else {
      bindId = this.selectedDevice.bindId;
    }
    this.certificateService
      //@ts-ignore
      .revokeDevice(bindId)
      .subscribe({
        next: async response => {
          this.loading = false;

          // Se tutto va bene

          if (!this.isPkBox) {
            this.toasterService.success(
              '',
              this.translateService.instant(
                'OTP_PAGE.APP.BIND_MODAL.REVOKE_SUCCESS'
              ),
              {
                positionClass: 'toast-top-right',
              }
            );
          } else {
            this.toasterService.success(
              '',
              this.translateService.instant(
                'OTP_PAGE.GOSIGN_MOBILE.BIND_MODAL.REVOKE_SUCCESS'
              ),
              {
                positionClass: 'toast-top-right',
              }
            );
          }

          this.closeModal();
          this.getPushDevices();
        },
        error: error => {
          console.error(error);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  showRevokeDialog(device: any) {
    this.selectedDevice = device;
    this.showRevoke = true;
  }

  get descText(): string {
    if (!this.isPkBox) {
      return this.hasApp
        ? 'OTP_PAGE.APP.DESC_WITH_BIND'
        : 'OTP_PAGE.APP.DESC_NO_BIND';
    } else {
      return this.hasApp
        ? 'OTP_PAGE.GOSIGN_MOBILE.DESC_WITH_BIND'
        : 'OTP_PAGE.GOSIGN_MOBILE.DESC_NO_BIND';
    }
  }

  get buttonText(): string {
    return this.translateService.instant(
      this.hasApp ? 'COMMON.BUTTON.MANAGE' : 'COMMON.ACTIVATE'
    );
  }

  get hardwareDescText(): string {
    return !this.showHWSuccess
      ? 'OTP_PAGE.HARDWARE.NEEDS_SYNC'
      : 'OTP_PAGE.HARDWARE.SYNCED';
  }

  onOtpSubmit() {
    this.loading = true;
    // stop here if form is invalid
    if (this.isTrissUser) {
      //se sono certificati triss
      if (this.otpForm.invalid) {
        this.trissOtpAttempted = true;
        this.otpForm.markAllAsTouched();
        this.loading = false;
        return;
      }

      const phoneValue = this.fTelephone['telephoneInput'].value;
      const telephoneInput = (phoneValue.prefix + phoneValue.number).replace(
        /\s+/g,
        ''
      );

      this.certificateService
        //@ts-ignore
        .changeTelephoneNumber(
          telephoneInput,
          this.fOtp['otp'].value.trim(),
          this.fOtp['confirmation_code'].value.trim()
        )
        .subscribe({
          next: async response => {
            this.loading = false;

            // Se tutto va bene
            this.telephone = telephoneInput;
            this.showSuccess = true;
          },
          error: error => {
            console.error(error);
            //this.error_state = true;
            this.clearForms();
            this.loading = false;
          },
          complete: () => {
            //this.error_state = false;
            this.loading = false;
            setTimeout(() => {
              this.closeModal();
            }, 5000);
            this.clearForms();
          },
        });
    } else {
      //se non sono triss
      if (this.notTrissOtpForm.invalid) {
        this.notTrissAttempted = true;
        this.notTrissOtpForm.markAllAsTouched();
        this.loading = false;
        return;
      }

      const phoneValue = this.fNotTrissOtp['notTrissTelephoneInput'].value;
      const telephoneInput = (phoneValue.prefix + phoneValue.number).replace(
        /\s+/g,
        ''
      );

      this.certificateService
        //@ts-ignore
        .changeTelephoneNumberNotTriss(
          telephoneInput,
          this.fNotTrissOtp['notTrissOtp'].value.trim()
        )
        .subscribe({
          next: async response => {
            this.loading = false;

            // Se tutto va bene
            this.telephone = telephoneInput;
            this.showSuccess = true;
          },
          error: error => {
            console.error(error);
            this.clearForms();
            this.loading = false;
          },
          complete: () => {
            this.loading = false;
            setTimeout(() => {
              this.closeModal();
            }, 5000);
            this.clearForms();
          },
        });
    }
  }

  clearForms() {
    //this.phoneInitiated = false;
    this.notTrissAttempted = false;
    this.trissPhoneAttempted = false;
    this.trissOtpAttempted = false;

    // Reset specifico per SMS e Triss inputs
    this.otpService.resetSmsOtpInputs();
    this.otpService.resetTrissOtpInputs();
    if (this.isTrissUser) {
      //se triss;
      this.telephoneInput = new UntypedFormControl(
        {
          prefix: '+39',
          number: '',
        },
        [
          phoneNumberRequired,
          (control: AbstractControl): ValidationErrors | null =>
            validPhoneNumberValidator(control),
        ]
      );

      this.telephoneForm = new UntypedFormGroup({
        telephoneInput: this.telephoneInput,
      });

      this.telephoneForm.valueChanges.subscribe(value => {
        this.number_text = value.telephoneInput.number;
        console.log(this.number_text);
      });

      this.otp = new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(8),
      ]);

      this.confirmation_code = new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(8),
      ]);

      this.otpForm = new UntypedFormGroup({
        otp: this.otp,
        confirmation_code: this.confirmation_code,
      });

      this.openStep(0);
    } else {
      // non triss
    this.notTrissTelephoneInput = new UntypedFormControl(
      {
        prefix: '+39',
        number: '',
      },
      [
        phoneNumberRequired,
        (control: AbstractControl): ValidationErrors | null =>
          validPhoneNumberValidator(control),
      ]
    );

      this.notTrissOtp = new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(8),
      ]);

      this.notTrissOtpForm = new UntypedFormGroup({
        notTrissTelephoneInput: this.notTrissTelephoneInput,
        notTrissOtp: this.notTrissOtp,
      });
    }
    this.setupValidationAttemptReset();

    //this.phoneInitiated = false;
    // setTimeout(() => {
    //   this.telephoneInput.markAsPristine();
    // }, 200);
    // this.telephoneInput.patchValue(' ');
    // this.phoneInitiated = false;
  }

  //invio otp e codice di conferma insieme
  sendOtpAndConfirmationCode() {
    const phoneValue = this.fTelephone['telephoneInput'].value;
    this.newMobile = (phoneValue.prefix + phoneValue.number).replace(
      /\s+/g,
      ''
    );
    console.log('telephone: ' + this.newMobile);
    this.otpService
      .generateCodeForOldAndNewMobile({ newMobile: this.newMobile })
      .subscribe({
        next: async response => {
          // Se tutto va bene
        },
        error: error => {
          console.error(error);
        },
        complete: () => {},
      });
  }

  //invio otp
  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  onHWSubmit() {
    this.loading = true;

    // stop here if form is invalid
    if (this.hwForm.invalid) {
      this.hwForm.markAllAsTouched();
      this.loading = false;
      return;
    }

    this.certificateService
      //@ts-ignore
      .synchronizeDevice(
        this.fHW['hwOtp1'].value.trim(),
        this.fHW['hwOtp2'].value.trim()
      )
      .subscribe({
        next: async response => {
          this.loading = false;

          // Se tutto va bene
          this.showHWSuccess = true;

          this.clearHWForm();
          this.closeModal();
        },
        error: error => {
          console.error(error);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  clearHWForm() {
    // Reset hardware OTP inputs
    this.otpService.resetHardwareOtpInputs();

    this.hwOtp1 = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);

    this.hwOtp2 = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);

    this.hwForm = new UntypedFormGroup({
      hwOtp1: this.hwOtp1,
      hwOtp2: this.hwOtp2,
    });
  }

  goToVirtualTokenPage() {
    this.router.navigate(['/otp/tokens']);
  }

  MigrationSteps: MigrationStep[] = [
    {
      step: 1,
      title: 'OTP_PAGE.SMS.MODAL.PHONE_LABEL',
      description: 'OTP_PAGE.SMS.MODAL.PHONE_ACCORDION_DESC',
      open: true,
      disabled: true,
    },
    {
      step: 2,
      title: 'OTP_PAGE.SMS.MODAL.OTP_ACCORDION_TITLE',
      description: 'OTP_PAGE.SMS.MODAL.OTP_ACCORDION_DESC',
      disabled: true,
    },
  ];

  //verifico se lo step è completato
  isStepComplete(step: number) {
    switch (step + 1) {
      case 1:
        return this.telephoneForm.valid;
      case 2:
        return this.otpForm.valid;
      default:
        return false;
    }
  }

  //apro lo step
  openStep(step: number) {
    if (step === 1 && this.telephoneForm.invalid) {
      this.trissPhoneAttempted = true;
      this.telephoneForm.markAllAsTouched();
      return;
    }
    if (step === 1) {
      this.trissPhoneAttempted = false;
    }
    this.MigrationSteps.forEach((migrationStep, index) => {
      if (index === step) {
        migrationStep.open = true;
      } else {
        migrationStep.open = false;
      }
    });
    if (step > -1) {
      this.currentStep = step;
    }
    if (this.currentStep === 1) {
      this.sendOtpAndConfirmationCode();
    }
  }

  //chiudo la modale
  closeModal() {
    this.showSuccess = false;
    this.showRevoke = false;
    // Chiudi tutte le modali standalone
    this.appModalVisible = false;
    this.smsModalVisible = false;
    this.hardwareModalVisible = false;
    this.virtualModalVisible = false;

    this.clearForms();
  }

  ngOnDestroy(): void {
    this.validationAttemptSubscriptions.forEach(sub => sub.unsubscribe());
    this.validationAttemptSubscriptions = [];
  }

  getDeviceIcon(subAuth: string): string {
    switch (subAuth) {
      case 'FIDO':
        return 'assets/images/phone-notification-icon.svg';
      case 'OTPS':
        return 'assets/images/phone-code-icon.svg';
      case 'QR_CODE':
        return 'assets/images/qr-code-icon.svg';
      default:
        return 'assets/images/phone-notification-icon.svg';
    }
  }

  getDeviceTitle(subAuth: string): string {
    if (this.isPkBox) {
      return 'OTP_PAGE.GOSIGN_MOBILE.BIND_MODAL.FIDO';
    } else {
      return `OTP_PAGE.APP.BIND_MODAL.${subAuth}`;
    }
  }

  getDeviceDesc(): string {
    if (this.isPkBox) {
      return 'OTP_PAGE.GOSIGN_MOBILE.BIND_MODAL.DEVICE';
    } else {
      return 'OTP_PAGE.APP.BIND_MODAL.DEVICE';
    }
  }

  // Metodi di reset specifici per uso pubblico
  resetAllOtpInputs() {
    this.otpService.resetAllOtpInputs();
  }

  resetSmsOtpInputs() {
    this.otpService.resetSmsOtpInputs();
  }

  resetTrissOtpInputs() {
    this.otpService.resetTrissOtpInputs();
  }

  resetHardwareOtpInputs() {
    this.otpService.resetHardwareOtpInputs();
  }

  onChange = (value: string) => {
    console.log('OTP value changed:', `"${value}"`, 'length:', value?.length);
  };

  // Event handlers for prefix number input
  onSelectChange(item: DropdownItem): void {
    console.log('Selected country prefix:', item);
  }

  onInputChange(value: string): void {
    console.log('Input phone number:', value);
  }
}
